import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { X } from '../../icons';
import {
  createTask, createProject, listProjects,
  TASK_PRIORITIES, type TaskPriority, type TaskProjectDto,
} from '../../api/tasks';
import { setTaskGroup, type TaskGroupDto } from '../../api/task-extras';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './MyTasksBoard.css';

interface MyTaskCreateModalProps {
  /** 그룹 셀렉트의 선택지 — 내 업무 화면이 이미 들고 있는 목록을 그대로 받는다. */
  groups: TaskGroupDto[];
  /** 목록에서 그룹 필터가 걸려 있으면 그 그룹을 기본값으로 연다. */
  initialGroupId?: string | null;
  onClose: () => void;
  onCreated: () => void;
}

/** 개인 업무를 담는 프로젝트 이름 — 기존 빠른 추가와 같은 규칙을 쓴다. */
export const PERSONAL_PROJECT_NAME = '내 할일';

/**
 * MyTaskCreateModal (2026-08-20) — 내 업무의 [업무 생성] 이 여는 모달.
 *
 * 종전에는 표 아래 한 줄짜리 입력칸(＋)에 제목만 적는 방식이었다. 제목 말고는 아무것도
 * 정할 수 없어서 만들고 나서 다시 상세로 들어가 고쳐야 했다. 여기서 한 번에 정한다:
 * 제목 · 어느 프로젝트에 넣을지 · 그룹 · 우선순위 · 마감일.
 *
 * 프로젝트를 고르지 않으면 개인 프로젝트('내 할일')로 간다 — 없으면 그때 만든다.
 * 태스크는 반드시 프로젝트에 속해야 하는데 "내 할일" 은 프로젝트 밖의 개념이라서다
 * (기존 빠른 추가와 같은 규칙 — 팀 프로젝트에는 손대지 않는다).
 *
 * 담당자는 지정하지 않는다. GET /tasks/me 가 "담당자=나 또는 (만든이=나 && 무배정)" 을
 * 내려주므로 만든 즉시 이 화면에 뜬다.
 */
export function MyTaskCreateModal({ groups, initialGroupId = null, onClose, onCreated }: MyTaskCreateModalProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const titleRef = useRef<HTMLInputElement | null>(null);

  const [title, setTitle] = useState('');
  const [projectId, setProjectId] = useState('');           // '' = 개인
  const [groupId, setGroupId] = useState(initialGroupId ?? '');
  const [priority, setPriority] = useState<TaskPriority>('MEDIUM');
  const [dueDate, setDueDate] = useState('');
  const [projects, setProjects] = useState<TaskProjectDto[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscapeClose(onClose, submitting);

  // 프로젝트 셀렉트 후보 — 실패해도 모달은 뜬다(개인으로 만들면 되므로).
  useEffect(() => {
    let alive = true;
    void listProjects()
      .then((rows) => { if (alive) setProjects(rows); })
      .catch(() => { /* 개인 선택만으로도 만들 수 있다 */ });
    return () => { alive = false; };
  }, []);

  /** 넣을 프로젝트 id — '개인' 이면 개인 프로젝트를 찾고, 없으면 만든다. */
  async function resolveProjectId(): Promise<string> {
    if (projectId) return projectId;
    const rows = projects.length > 0 ? projects : await listProjects();
    const personal = rows.find((p) => !p.teamId && p.name === PERSONAL_PROJECT_NAME);
    if (personal) return personal.id;
    return (await createProject({ name: PERSONAL_PROJECT_NAME })).id;
  }

  async function submit() {
    const t = title.trim();
    // 비활성 버튼 대신 이유를 말한다(TaskGroupCreateModal 과 같은 규칙).
    if (!t) {
      setError(tProject('project.mytask.create_err_title'));
      titleRef.current?.focus();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const pid = await resolveProjectId();
      const task = await createTask(pid, {
        title: t,
        priority,
        ...(dueDate ? { dueDate } : {}),
      });
      // 그룹은 업무를 만든 뒤 붙인다(생성 API 에 그룹 필드가 없다). 실패해도 업무는 남는다.
      if (groupId) {
        try { await setTaskGroup(task.id, groupId); } catch { /* 그룹만 미지정으로 남는다 */ }
      }
      onCreated();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (err as Error).message;
      setError(msg ?? tProject('project.mytask.err_add'));
      setSubmitting(false);
    }
  }

  return (
    <div
      className="pj-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tProject('project.mytask.create_title')}
    >
      <div ref={trapRef} className="pj-modal mtg-modal">
        <header className="mtg-head">
          <h2>{tProject('project.mytask.create_title')}</h2>
          <button type="button" className="mtg-close" onClick={onClose} disabled={submitting}>
            <X size={14} aria-hidden />
            {tProject('project.btn.close')}
          </button>
        </header>

        <label className="mtg-label" htmlFor="mtc-title">{tProject('project.mytask.create_label_title')}</label>
        <input
          id="mtc-title"
          ref={titleRef}
          className="mtg-input"
          aria-invalid={error != null && !title.trim()}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={tProject('project.mytask.create_ph_title')}
          maxLength={200}
          autoFocus
          disabled={submitting}
          onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
        />

        <label className="mtg-label mtg-label--mt" htmlFor="mtc-project">
          {tProject('project.mytask.create_label_project')}
        </label>
        <select
          id="mtc-project"
          className="mtg-input"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          disabled={submitting}
        >
          <option value="">{tProject('project.mytask.create_personal')}</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>

        <label className="mtg-label mtg-label--mt" htmlFor="mtc-group">
          {tProject('project.mytask.create_label_group')}
        </label>
        <select
          id="mtc-group"
          className="mtg-input"
          value={groupId}
          onChange={(e) => setGroupId(e.target.value)}
          disabled={submitting}
        >
          <option value="">{tProject('project.mytask.group_none')}</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>

        <div className="mtc-row">
          <div className="mtc-col">
            <label className="mtg-label mtg-label--mt" htmlFor="mtc-priority">
              {tProject('project.mytask.create_label_priority')}
            </label>
            <select
              id="mtc-priority"
              className="mtg-input"
              value={priority}
              onChange={(e) => setPriority(e.target.value as TaskPriority)}
              disabled={submitting}
            >
              {TASK_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {tProject(`project.mytask.pri_${p.toLowerCase()}` as never)}
                </option>
              ))}
            </select>
          </div>
          <div className="mtc-col">
            <label className="mtg-label mtg-label--mt" htmlFor="mtc-due">
              {tProject('project.mytask.create_label_due')}
            </label>
            <input
              id="mtc-due"
              type="date"
              className="mtg-input"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              disabled={submitting}
            />
          </div>
        </div>

        {error && <div className="pj-modal-error" role="alert">{error}</div>}

        <footer className="mtg-foot">
          <button type="button" className="mtg-submit" onClick={() => void submit()} disabled={submitting}>
            {submitting ? tProject('project.btn.saving') : tProject('project.mytask.create_submit')}
          </button>
          <button type="button" className="mtg-cancel" onClick={onClose} disabled={submitting}>
            {tProject('project.btn.cancel')}
          </button>
        </footer>
      </div>
    </div>
  );
}
